import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaPlus,
  FaMinus,
  FaQuestionCircle,
  FaArrowLeft
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getData } from '../../../../Services/Fetchnodeservices';

const RestaurantFAQ = ({ isDark = false }) => {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  // 🎨 ZAPTODOOR ORANGE THEME CONFIGURATION
  const theme = isDark ? {
    background: '#0f172a',
    card: '#1e293b',
    primary: '#f97316',
    primaryLight: '#fb923c',
    textPrimary: '#f8fafc',
    textSecondary: '#94a3b8',
    border: 'rgba(249, 115, 22, 0.2)',
    inputBackground: '#0f172a',
    inputText: '#f8fafc',
    inputPlaceholder: '#94a3b8',
    divider: 'rgba(249, 115, 22, 0.2)',
    textMuted: '#64748b',
    shadow: '0 4px 12px rgba(0,0,0,0.3)',
    shadowActive: '0 8px 24px rgba(249, 115, 22, 0.15)',
  } : {
    background: '#fff7ed',
    card: '#ffffff',
    primary: '#f97316',
    primaryLight: '#fb923c',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    border: '#fed7aa',
    inputBackground: '#ffffff',
    inputText: '#0f172a',
    inputPlaceholder: '#94a3b8',
    divider: '#fed7aa',
    textMuted: '#94a3b8',
    shadow: '0 1px 3px rgba(0,0,0,0.06)',
    shadowActive: '0 8px 24px rgba(249, 115, 22, 0.12)',
  };

  const fetchFAQs = async () => {
    setLoading(true);
    try {
      const url = `faqRouter/?category=Resturant&_=${Date.now()}`;
      const data = await getData(url);
      if (data.success && data.data.length > 0) {
        const mapped = data.data.map(item => ({
          id: item.id,
          question: item.ques,
          answer: item.ans,
        }));
        setFaqs(mapped);
        setFilteredFaqs(mapped);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFAQs();
  }, []);

  useEffect(() => {
    const lower = search.toLowerCase();
    const filtered = faqs.filter(f =>
      f.question.toLowerCase().includes(lower) ||
      f.answer.toLowerCase().includes(lower)
    );
    setFilteredFaqs(filtered);
  }, [search, faqs]);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const renderHTML = (html) => {
    return { __html: html };
  };

  if (loading) {
    return (
      <div style={{...styles.loaderContainer, backgroundColor: theme.background}}>
        <div style={styles.loader}>
          <div style={{...styles.spinner, borderTopColor: theme.primary, borderColor: theme.border}}></div>
          <p style={{ color: theme.textSecondary, marginTop: 12, fontSize: '15px', fontWeight: 500 }}>Loading FAQs...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, backgroundColor: theme.background }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button 
            className="back-btn-small"
            onClick={() => navigate(-1)}
          >
            <FaArrowLeft size={18} />
          </button>
          <h1 style={{ ...styles.headerTitle, color: theme.textPrimary }}>
            Help & <span style={{ color: theme.primary }}>Support</span>
          </h1>
          <p style={{ ...styles.headerSub, color: theme.textSecondary }}>
            Everything you need to know about ZaptoDoor
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{ 
        ...styles.searchBox, 
        backgroundColor: theme.inputBackground, 
        borderColor: theme.border,
        boxShadow: theme.shadow
      }}>
        <FaSearch size={18} color={theme.primary} />
        <input
          style={{ ...styles.searchInput, color: theme.inputText }}
          placeholder="How can we help you today?"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* FAQ List */}
      <div style={styles.listContainer}>
        {filteredFaqs.length === 0 ? (
          <div style={styles.emptyState}>
            <FaQuestionCircle size={48} color={theme.textMuted} />
            <p style={{ color: theme.textMuted, marginTop: 12, fontSize: '16px', fontWeight: 600 }}>No results found</p>
            <p style={{ color: theme.textSecondary, marginTop: 4, fontSize: '14px' }}>Try adjusting your search terms</p>
          </div>
        ) : (
          filteredFaqs.map((item) => {
            const isExpanded = expandedId === item.id;
            return (
              <div
                key={item.id}
                className="faq-card"
                style={{
                  ...styles.card,
                  backgroundColor: theme.card,
                  borderColor: isExpanded ? theme.primary : theme.border,
                  boxShadow: isExpanded ? theme.shadowActive : theme.shadow,
                }}
              >
                <button
                  style={styles.questionRow}
                  onClick={() => toggleExpand(item.id)}
                >
                  <span style={{ ...styles.questionText, color: theme.textPrimary }}>
                    {item.question}
                  </span>
                  <div style={{
                    ...styles.iconCircle,
                    backgroundColor: isExpanded ? theme.primary : (isDark ? 'rgba(255,255,255,0.05)' : '#fff7ed'),
                  }}>
                    {isExpanded ? (
                      <FaMinus size={14} color="#ffffff" />
                    ) : (
                      <FaPlus size={14} color={theme.primary} />
                    )}
                  </div>
                </button>

                {isExpanded && (
                  <div style={{ ...styles.answerContainer, borderTopColor: theme.divider }}>
                    <div
                      className="faq-answer"
                      style={{
                        ...styles.answerContent,
                        color: theme.textSecondary,
                      }}
                      dangerouslySetInnerHTML={renderHTML(item.answer)}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Animation CSS */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spinner {
          border: 3px solid ${theme.border};
          border-top: 3px solid ${theme.primary};
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 0.8s linear infinite;
        }
        .back-btn-small {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          border: 1.5px solid ${theme.border};
          background: ${isDark ? 'rgba(255,255,255,0.05)' : '#fff7ed'};
          color: ${theme.primary};
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 16px;
        }
        .back-btn-small:hover {
          background: ${isDark ? 'rgba(249, 115, 22, 0.15)' : '#ffedd5'};
          transform: translateX(-3px);
        }
        .faq-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .faq-card:hover {
          transform: translateY(-2px);
        }
        .faq-answer p {
          margin: 0 0 10px 0;
        }
        .faq-answer p:last-child {
          margin-bottom: 0;
        }
        .faq-answer strong {
          color: ${theme.primary};
          font-weight: 600;
        }
        .faq-answer a {
          color: ${theme.primary};
          text-decoration: none;
          font-weight: 500;
        }
        .faq-answer a:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    padding: '30px 20px 60px 20px',
    transition: 'background-color 0.3s ease',
  },
  loaderContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease',
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '0 0 20px 0',
    maxWidth: '800px',
    margin: '0 auto',
    width: '100%',
  },
  headerLeft: {
    width: '100%',
  },
  headerTitle: {
    fontSize: '28px',
    fontWeight: 800,
    margin: 0,
    letterSpacing: '-0.5px',
  },
  headerSub: {
    fontSize: '15px',
    margin: '8px 0 0 0',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '800px',
    margin: '0 auto 24px auto',
    padding: '0 20px',
    height: '56px',
    borderRadius: '16px',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    width: '100%',
    boxSizing: 'border-box',
    transition: 'all 0.3s ease',
  },
  searchInput: {
    flex: 1,
    marginLeft: '12px',
    fontSize: '16px',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    height: '100%',
    fontFamily: 'inherit',
  },
  listContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    width: '100%',
  },
  card: {
    borderRadius: '16px',
    marginBottom: '12px',
    padding: '20px 24px',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    cursor: 'pointer',
  },
  questionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
  },
  questionText: {
    flex: 1,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '1.5',
    paddingRight: '16px',
  },
  iconCircle: {
    width: '32px',
    height: '32px',
    borderRadius: '10px', // Modern rounded square instead of circle
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.3s ease',
  },
  answerContainer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
  },
  answerContent: {
    fontSize: '15px',
    lineHeight: '1.7',
    fontFamily: 'inherit',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    marginTop: '20px',
  },
};

export default RestaurantFAQ;